/* eslint-disable no-lonely-if */
/* eslint-disable react/jsx-no-constructed-context-values */

import React, { createContext, useState } from 'react';
import {
  OrderData,
  OrderStep,
  Prescription,
  Product,
  ShopifyOrderData,
  ShopifyProduct,
} from '../../utils/types';
import {
  getDefaultVariantId,
  populateMainItemProperties,
  productToOrderItem,
  shopifyProdToProduct,
  typeToLastStep,
} from '../../utils/shopify.utils';
import { combinedProducts, glassesNeedingPrescription } from '../../config/settings';
import { get } from '../../api/requests';

export const initOrderData: OrderData = {
  glasses: null,
  glassExtra: null,
  upsells: [],
};
interface ParentProduct {
  handle: string;
  variantId: number;
  price: number;
  type: string;
}

const initParentProduct: ParentProduct = {
  handle: '',
  variantId: 0,
  price: 0,
  type: '',
};

export const initPrescription: Prescription = {
  title: '',
  data: '',
};

export interface IOrderContextProps {
  parentProduct: ParentProduct;
  setParentProduct: (product: ParentProduct) => void;
  currentStep: number;
  advanceOrderStep: () => void;
  revertOrderStep: () => void;
  isLastStep: () => boolean;
  orderData: OrderData;
  setOrderData: (product: Product, isGlasses?: boolean) => void;
  getShopifyOrderData: () => ShopifyOrderData | null;
  getOrderPrice: () => number;
  getProductIsInOrder: (product: Product, isGlasses?: boolean) => boolean;
  prescription: Prescription;
  setPrescription: (prescription: Prescription) => void;
  resetContext: () => void;
}
export const OrderContext = createContext({
  parentProduct: initParentProduct,
  currentStep: 1,
  orderData: initOrderData,
  prescription: initPrescription,
  setParentProduct: () => {},
  setOrderData: () => {},
  setPrescription: () => {},
  getProductIsInOrder: () => false,
  getOrderPrice: () => 0,
  getShopifyOrderData: () => null,
  advanceOrderStep: () => {},
  revertOrderStep: () => {},
  isLastStep: () => false,
  resetContext: () => {},
} as IOrderContextProps);
type ContextProviderProps = {
  state?: { [key: string]: string };
} & React.HTMLAttributes<HTMLElement>;
export function ContextProvider({ children }: ContextProviderProps) {
  const firstStep = OrderStep.Glasses;
  const [currentStep, setCurrentStep] = useState(firstStep);
  const [data, setData] = useState(initOrderData);
  const [prescription, setPrescription] = useState(initPrescription);
  const [parentProduct, setParentProduct] = useState(initParentProduct);

  const setOrderData = async (product: Product, isGlasses = true) => {
    if (isGlasses) {
      if (combinedProducts[`${product.handle}`]) {
        const combinedProduct: ShopifyProduct = await get(
          `/products/${combinedProducts[`${product.handle}`]}.js`
        );
        return setData((prevData) => ({
          ...prevData,
          glasses: product,
          glassExtra: shopifyProdToProduct(combinedProduct),
        }));
      }
      return setData((prevData) => ({ ...prevData, glasses: product, glassExtra: null }));
    }
    let newUpsells = JSON.parse(JSON.stringify(data.upsells));
    if (data.upsells.some((prod) => prod.id === product.id)) {
      newUpsells = data.upsells.filter((upsell) => upsell.id !== product.id);
      return setData((prevData) => ({ ...prevData, upsells: newUpsells }));
    }
    newUpsells.push(product);
    setData((prevData) => ({ ...prevData, upsells: newUpsells }));
  };

  const getProductIsInOrder = (product: Product, isGlasses = true) => {
    if (isGlasses) {
      return data.glasses ? product.id === data.glasses.id : false;
    }
    return !!data.upsells.find((upsell) => upsell.id === product.id);
  };

  const getOrderPrice = () => {
    const basePrice: number = parentProduct.price ? +parentProduct.price : +0;
    const glassPrice: number = data.glasses?.price ? +data.glasses.price : +0;
    const upsellsPrice = data.upsells
      .map((prod) => (prod.price ? prod.price : 0))
      .reduce((a, b) => a + b, 0);
    return basePrice + glassPrice + upsellsPrice;
  };

  const getShopifyOrderData = (): ShopifyOrderData => {
    if (!data.glasses) return { items: [] };
    const mainItem = productToOrderItem(parentProduct.variantId, 1);
    populateMainItemProperties({
      mainItem,
      glasses: data.glasses,
      upsells: data.upsells,
      glassExtra: data.glassExtra,
      prescription,
    });
    const orderItems = [mainItem];
    if (data.glasses.id && glassesNeedingPrescription.includes(data.glasses.id)) {
      orderItems.push(productToOrderItem(getDefaultVariantId(data.glasses), 1));
      if (data.glassExtra) {
        orderItems.push(productToOrderItem(getDefaultVariantId(data.glassExtra), 1));
      }
    }
    data.upsells.forEach((product) => {
      orderItems.push(productToOrderItem(getDefaultVariantId(product), 1));
    });
    return { items: orderItems };
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
    window.scrollTo(0, 0);
  };
  const advanceOrderStep = () => {
    if (
      currentStep === OrderStep.Glasses &&
      !glassesNeedingPrescription.includes(data.glasses?.id || 0)
    ) {
      // skip prescription
      return goToStep(currentStep + 2);
    }
    goToStep(currentStep + 1);
  };
  const revertOrderStep = () => {
    if (
      currentStep === OrderStep.Options &&
      !glassesNeedingPrescription.includes(data.glasses?.id || 0)
    ) {
      // skip prescription
      return goToStep(currentStep - 2);
    }
    goToStep(currentStep - 1);
  };
  const isLastStep = () => {
    return typeToLastStep(parentProduct.type, data.glasses) === currentStep;
  };

  const resetContext = () => {
    setCurrentStep(firstStep);
    setData(initOrderData);
    setParentProduct(initParentProduct);
  };

  const context = {
    parentProduct,
    currentStep,
    orderData: data,
    prescription,
    setParentProduct,
    advanceOrderStep,
    revertOrderStep,
    isLastStep,
    setOrderData,
    setPrescription,
    getProductIsInOrder,
    getOrderPrice,
    getShopifyOrderData,
    resetContext,
  };

  return <OrderContext.Provider value={context}>{children}</OrderContext.Provider>;
}
